import { LEVERS, VICTORY, type LeverId } from '@sd/content';
import { sameTeam, windowOutput, type Command, type GameState, type PlayerState } from '@sd/sim';
import type { Bot } from './match';

const usable = (self: PlayerState, lever: LeverId) =>
  self.techs.includes(LEVERS[lever].requiresTech) && self.influence >= LEVERS[lever].cost;

/** The reserve issuer whose output share is within a few points of the economic victory. */
function nearEconomicVictory(state: GameState): string | null {
  const issuer = state.reserve.issuer;
  if (issuer === null) return null;
  const outputs = state.players.map((p) => windowOutput(p.currency));
  const total = outputs.reduce((a, b) => a + b, 0);
  const i = state.players.findIndex((p) => p.id === issuer);
  const need = VICTORY.economicShare[state.players.length] ?? 0.5;
  return total > 0 && outputs[i]! / total >= need - WARNING_MARGIN ? issuer : null;
}

/** How close to the economic threshold a rival gets before the coalition moves. */
const WARNING_MARGIN = 0.05;

/** The rival with the largest output over the currency window, other than `self`. */
function leader(state: GameState, self: PlayerState): PlayerState | null {
  const out = (p: PlayerState) => p.currency.outputValue.reduce((a, b) => a + b, 0);
  const others = state.players.filter(
    (p) => p.id !== self.id && !sameTeam(state.players, self.id, p.id),
  );
  others.sort((a, b) => out(b) - out(a) || a.id.localeCompare(b.id));
  return others[0] ?? null;
}

/**
 * Economic levers (GDD §6): sanctions on whoever counts down to victory
 * (joining a coalition if one gathers), a standing tariff, and an embargo on
 * the leading rival when influence piles up. `aggressive` traders use them all;
 * others only join sanctions against a countdown.
 */
export function makeStatesman(aggressive: boolean): Bot {
  return (state, playerId, rng) => {
    if (state.pause || !rng.chance(0.03)) return [];
    const self = state.players.find((p) => p.id === playerId);
    if (!self || self.vassalOf !== null) return [];
    const gathering = state.levers.find(
      (l) =>
        l.kind === 'sanctions' &&
        l.status === 'gathering' &&
        l.target !== playerId &&
        !sameTeam(state.players, playerId, l.target!) &&
        !l.by.includes(playerId),
    );
    if (gathering && self.influence >= LEVERS.sanctions.cost) {
      return [{ type: 'joinSanctions', playerId, leverId: gathering.id }];
    }
    // A countdown, or a reserve issuer close to the economic victory (GDD §10), is the threat.
    const near = nearEconomicVictory(state);
    // A teammate's victory is ours too (#137).
    const rival = (id: string) => id !== playerId && !sameTeam(state.players, playerId, id);
    const threat =
      state.countdowns.find((c) => rival(c.playerId)) ??
      (near && rival(near) ? { playerId: near } : undefined);
    const sanctioned = (target: string) =>
      state.levers.some((l) => l.kind === 'sanctions' && l.target === target);
    if (threat && usable(self, 'sanctions') && !sanctioned(threat.playerId)) {
      return [{ type: 'useLever', playerId, lever: 'sanctions', target: threat.playerId }];
    }
    if (!aggressive) return [];
    const commands: Command[] = [];
    const mine = (kind: LeverId) =>
      state.levers.some((l) => l.kind === kind && l.by.includes(playerId));
    if (usable(self, 'tariff') && !mine('tariff')) {
      commands.push({ type: 'useLever', playerId, lever: 'tariff' });
    } else if (self.influence >= 70 && usable(self, 'embargo') && !mine('embargo')) {
      const rival = leader(state, self);
      if (rival) commands.push({ type: 'useLever', playerId, lever: 'embargo', target: rival.id });
    }
    return commands;
  };
}
